import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, User, Mail, Lock, Store, Building2, Rocket, Globe, TrendingUp, Smartphone, Check, X as XIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { getBusinessTypesPublic } from '../services/api';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    business_name: '',
    business_type_id: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    getBusinessTypesPublic()
      .then((res) => setBusinessTypes(res.data))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }
    if (!/[A-Z]/.test(formData.password)) {
      toast.error('La contraseña debe tener al menos una letra mayúscula');
      setLoading(false);
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(formData.password)) {
      toast.error('La contraseña debe tener al menos un signo especial');
      setLoading(false);
      return;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        business_name: formData.business_name,
        business_type_id: formData.business_type_id || undefined,
        role: 'business_owner',
      });

      toast.success('¡Cuenta creada! Verifica tu correo electrónico.');
      navigate('/verify-email');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(getErrorMessage(error, 'Error al registrarse'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Globe, title: 'Presencia en línea', desc: 'Tu negocio visible en el marketplace' },
    { icon: Smartphone, title: 'Punto de venta', desc: 'POS profesional desde cualquier dispositivo' },
    { icon: TrendingUp, title: 'Reportes y analytics', desc: 'Métricas de ventas en tiempo real' },
    { icon: Rocket, title: 'Gratis para empezar', desc: 'Sin costos de activación ni mensualidades' },
  ];

  const inputClass = "w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm";
  const ringStyle = { '--tw-ring-color': COLORS.navyBlue + '40' };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center py-8 px-4">
        <div className="w-full max-w-4xl mb-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al marketplace
          </Link>
        </div>

        <div className="w-full max-w-4xl flex rounded-2xl overflow-hidden shadow-sm bg-white">
          {/* Left panel - benefits (desktop only) */}
          <div className="hidden lg:flex flex-col justify-center w-[380px] p-10" style={{ backgroundColor: COLORS.navyBlue }}>
            <h2 className="text-2xl font-bold text-white mb-1">
              Vende en Todo En Local
            </h2>
            <p className="text-blue-200 text-sm mb-8">
              Lleva tu negocio al siguiente nivel
            </p>
            <div className="space-y-5">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">{b.title}</h3>
                    <p className="text-blue-200 text-xs mt-0.5">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel - form */}
          <div className="flex-1 p-6 sm:p-10">
            <div className="max-w-sm mx-auto">
              <div className="flex flex-col items-center mb-6">
                <img src="/logoenlocal.png" alt="Todo en Local" className="h-14 w-auto mb-3" />
                <h2 className="text-xl font-bold" style={{ color: COLORS.darkSlate }}>
                  Registra tu <span style={{ color: COLORS.navyBlue }}>negocio</span>
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Crea tu cuenta y empieza a vender
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Nombre completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="full_name"
                      name="full_name"
                      type="text"
                      required
                      data-testid="register-name-input"
                      value={formData.full_name}
                      onChange={handleChange}
                      className={inputClass}
                      style={ringStyle}
                      placeholder="Juan Pérez"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="business_name" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Nombre del negocio
                  </label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="business_name"
                      name="business_name"
                      type="text"
                      required
                      data-testid="register-business-input"
                      value={formData.business_name}
                      onChange={handleChange}
                      className={inputClass}
                      style={ringStyle}
                      placeholder="Mi Tienda Local"
                    />
                  </div>
                </div>

                {businessTypes.length > 0 && (
                  <div>
                    <label htmlFor="business_type_id" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Tipo de negocio
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <select
                        id="business_type_id"
                        name="business_type_id"
                        value={formData.business_type_id}
                        onChange={handleChange}
                        className={`${inputClass} appearance-none`}
                        style={ringStyle}
                      >
                        <option value="">Selecciona un tipo</option>
                        {businessTypes.map((bt) => (
                          <option key={bt.id} value={bt.id}>
                            {bt.icon ? `${bt.icon} ` : ''}{bt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="register-email-input"
                      value={formData.email}
                      onChange={handleChange}
                      className={inputClass}
                      style={ringStyle}
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      data-testid="register-password-input"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                      style={ringStyle}
                      placeholder="Mínimo 6 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {formData.password.length > 0 && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      <span className={`flex items-center gap-1 text-xs ${formData.password.length >= 8 ? 'text-green-600' : 'text-slate-400'}`}>
                        {formData.password.length >= 8 ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                        8+ caracteres
                      </span>
                      <span className={`flex items-center gap-1 text-xs ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-slate-400'}`}>
                        {/[A-Z]/.test(formData.password) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                        1 mayúscula
                      </span>
                      <span className={`flex items-center gap-1 text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(formData.password) ? 'text-green-600' : 'text-slate-400'}`}>
                        {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(formData.password) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                        1 signo especial
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirm ? 'text' : 'password'}
                      required
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow sm:text-sm"
                      style={ringStyle}
                      placeholder="Repite tu contraseña"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {formData.confirm_password.length > 0 && (
                    <div className="mt-2">
                      <span className={`flex items-center gap-1 text-xs ${formData.password === formData.confirm_password ? 'text-green-600' : 'text-red-500'}`}>
                        {formData.password === formData.confirm_password ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                        {formData.password === formData.confirm_password ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  data-testid="register-submit-button"
                  disabled={loading}
                  className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
                </button>

                <div className="text-center pt-1">
                  <p className="text-sm text-slate-500">
                    ¿Ya tienes cuenta?{' '}
                    <Link to="/login" className="font-semibold" style={{ color: COLORS.orange }}>
                      Inicia sesión
                    </Link>
                  </p>
                </div>
              </form>

              {/* Customer link */}
              <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                <p className="text-sm text-slate-500">
                  ¿Buscas comprar productos?{' '}
                  <Link to="/auth" className="font-semibold" style={{ color: COLORS.navyBlue }}>
                    Ingresa como cliente
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
